import React, { useState, useEffect, useMemo } from "react";
import { observer, Observer } from "mobx-react-lite";
import { Dropdown, Button, Menu, Select } from "antd";
import { DeleteOutlined, PlusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import BuilderInteractor from "./builder.interactor";

// Import all components
import { ExpressionRenderer } from "./components/ExpressionRenderer";
import type { MenuProps } from 'antd';

const { Option } = Select;

const expressionTemplates = [
  "exp1", "exp2", "exp3", "exp4", "exp5", 
  "exp5-2", "exp5-3", "exp5-4", "exp6", 
  "exp7"
];

const UpdateMicrolet: React.FC = observer(() => {
  const interactor = useMemo(() => new BuilderInteractor(), []);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState("exp1");

  const loadExpression = async (expressionName: string) => {
    try {
      setLoading(true);
      const expressionModule = await import(`./data/${expressionName}.json`);
      const expressionData = expressionModule.default;
      interactor.setExpression(expressionData);
    } catch (error) {
      console.error("Error loading expression:", error);
      interactor.setExpression({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpression(selectedTemplate);
  }, [selectedTemplate]);

  const addExpression = (type: string) => {
    interactor.addExpression(type);
  };

  const menuItems: MenuProps['items'] = [
    {
      key: "AND",
      label: "AND",
      onClick: () => addExpression("AND")
    },
    {
      key: "OR",
      label: "OR",
      onClick: () => addExpression("OR")
    },
    {
      key: "CASE",
      label: "CASE",
      onClick: () => addExpression("CASE")
    },
    {
      key: "Literal Value",
      label: "Literal Value",
      onClick: () => addExpression("LiteralValue")
    },
    {
      key: "Data Field Path",
      label: "Data Field Path",
      onClick: () => addExpression("DataFieldPath")
    },
    {
      key: "Functions",
      label: "Functions",
      onClick: () => addExpression("Function")
    },
    {
      key: "Unary Arithmetic",
      label: "Unary Arithmetic",
      onClick: () => addExpression("UnaryArithmetic")
    }
  ];

  const handleNewExpression = () => {
    interactor.resetExpression();
    setSelectedTemplate(""); // Clear template selection
  };

  if (loading) {
    return <div>Loading expression...</div>;
  }

  return (
    <div className="update-microlet">
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '20px',
        marginBottom: '20px' 
      }}>
        <h1>Expression Builder</h1>
        <Select
          value={selectedTemplate}
          onChange={setSelectedTemplate}
          style={{ width: 200, marginLeft: 0 }}
          placeholder="Select Template"
        >
          {expressionTemplates.map(name => (
            <Option key={name} value={name}>
              Expression {name}
            </Option>
          ))}
        </Select>
        <Button 
          type="primary"
          onClick={handleNewExpression}
          style={{ marginLeft: 0 }}
        >New Expression
        </Button>

        {/* <Button 
          danger
          onClick={() => interactor.resetExpression()}
          icon={<DeleteOutlined />}
        >
          Reset
        </Button> */}
      </div>

      <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
        <PlusCircleOutlined />
      </Dropdown>
      <Observer>
        {() => {
          const expr = interactor.getExpression();
          return expr ? (
            <div className="expression-renderer-container">
              {expr.operator && (
                <div><input type="checkbox" checked={expr.operator === "NOT"} onChange={() => interactor.toggleUnaryLogicalObject([])} /></div>
              )}
              <ExpressionRenderer
                requiredExpMenu={true}
                expr={expr}
                path={[]}
                interactor={interactor}
              />
              {expr.operator && (
                <div><DeleteOutlined onClick={() => interactor.resetExpression()} /></div>
              )}
            </div>
          ) : null;
        }}
      </Observer>
      <div className="expression-json" style={{
        position: 'fixed',
        right: '0',
        top: '0',
        width: '400px',
        height: '100vh',
        backgroundColor: 'rgb(235 235 235)',
        padding: '20px',
        overflowY: 'auto',
        boxShadow: '-2px 0 5px rgba(0,0,0,0.1)'
       
      }}>
        <h2>Current Expression (JSON):</h2>
        <pre style={{ 
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word'
        }}>
          {JSON.stringify(interactor.getExpression(), null, 2)}
        </pre>
      </div>
    </div>
  );
});

export default UpdateMicrolet;
